import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProjectStore } from '../store/project-store';
import { useTranslation } from 'react-i18next';
const TYPE_ICONS = {
    project: '📊',
    task: '⏱️',
    sprint: '🏃',
    epic: '📈',
};
const STATUS_COLORS = {
    'backlog': 'bg-gray-100 text-gray-700',
    'to-do': 'bg-blue-100 text-blue-700',
    'in-progress': 'bg-yellow-100 text-yellow-700',
    'in-review': 'bg-purple-100 text-purple-700',
    'done': 'bg-green-100 text-green-700',
    'planned': 'bg-gray-100 text-gray-700',
    'completed': 'bg-green-100 text-green-700',
    'active': 'bg-green-100 text-green-700',
    'archived': 'bg-gray-100 text-gray-700',
};
function highlightMatch(text, query) {
    if (!query)
        return text;
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) => regex.test(part) ? (_jsx("span", { className: "bg-yellow-200 text-yellow-900", children: part }, i)) : (part));
}
export function GlobalSearch() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(-1);
    const [isOpen, setIsOpen] = useState(false);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const projects = useProjectStore((s) => s.projects);
    const tasks = useProjectStore((s) => s.tasks);
    const sprints = useProjectStore((s) => s.sprints);
    const epics = useProjectStore((s) => s.epics);
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(query);
        }, 300);
        return () => clearTimeout(timer);
    }, [query]);
    const results = useMemo(() => {
        if (!debouncedQuery)
            return [];
        const q = debouncedQuery.toLowerCase();
        const items = [];
        projects.forEach((p) => {
            if (p.name.toLowerCase().includes(q) ||
                p.tags?.some((t) => t.toLowerCase().includes(q))) {
                items.push({
                    type: 'project',
                    id: p.project_id,
                    title: p.name,
                    subtitle: p.tags?.join(', '),
                    status: p.status,
                });
            }
        });
        tasks.forEach((t) => {
            if (t.title.toLowerCase().includes(q) ||
                t.description.toLowerCase().includes(q)) {
                items.push({
                    type: 'task',
                    id: t.id,
                    projectId: t.projectId,
                    title: t.title,
                    subtitle: t.description.slice(0, 60) + (t.description.length > 60 ? '...' : ''),
                    status: t.status,
                });
            }
        });
        sprints.forEach((s) => {
            if (s.name.toLowerCase().includes(q) || s.goal.toLowerCase().includes(q)) {
                items.push({
                    type: 'sprint',
                    id: s.id,
                    projectId: s.projectId,
                    title: s.name,
                    subtitle: s.goal,
                    status: s.status,
                });
            }
        });
        epics.forEach((e) => {
            if (e.title.toLowerCase().includes(q)) {
                items.push({
                    type: 'epic',
                    id: e.id,
                    projectId: e.projectId,
                    title: e.title,
                    status: e.status,
                });
            }
        });
        return items.slice(0, 20);
    }, [debouncedQuery, projects, tasks, sprints, epics]);
    const handleSelect = useCallback((result) => {
        if (result.projectId) {
            navigate(`/board/${result.projectId}`);
        }
        else if (result.type === 'project') {
            navigate(`/board/${result.id}`);
        }
        setIsOpen(false);
        setQuery('');
        setActiveIndex(-1);
    }, [navigate]);
    const handleKeyDown = useCallback((e) => {
        if (!isOpen)
            return;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
                break;
            case 'Enter':
                e.preventDefault();
                if (activeIndex >= 0 && results[activeIndex]) {
                    handleSelect(results[activeIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setActiveIndex(-1);
                break;
        }
    }, [isOpen, activeIndex, results, handleSelect]);
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current &&
                !dropdownRef.current.contains(e.target) &&
                inputRef.current &&
                !inputRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    useEffect(() => {
        setActiveIndex(-1);
    }, [debouncedQuery]);
    const groupedResults = useMemo(() => {
        const groups = {
            project: [],
            task: [],
            sprint: [],
            epic: [],
        };
        results.forEach((r) => groups[r.type].push(r));
        return groups;
    }, [results]);
    const hasResults = results.length > 0;
    const showDropdown = isOpen && debouncedQuery;
    return (_jsxs("div", { className: "relative", children: [_jsx("input", { ref: inputRef, type: "text", placeholder: t('globalSearch.placeholder'), value: query, onChange: (e) => {
                    setQuery(e.target.value);
                    setIsOpen(true);
                }, onFocus: () => setIsOpen(true), onKeyDown: handleKeyDown, className: "input w-64", "aria-label": t('globalSearch.placeholder'), role: "combobox", "aria-expanded": showDropdown ? true : undefined, "aria-controls": "search-results-list" }), showDropdown && (_jsx("div", { ref: dropdownRef, className: "absolute top-full left-0 right-0 mt-1 bg-bg-primary border border-card-border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto", id: "search-results-list", role: "listbox", "aria-label": t('common.search'), children: hasResults ? (_jsx("div", { className: "py-1", children: Object.keys(groupedResults).map((type) => {
                        const items = groupedResults[type];
                        if (items.length === 0)
                            return null;
                        return (_jsxs("div", { children: [_jsx("div", { className: "px-3 py-1 text-xs font-semibold text-text-muted uppercase", children: type === 'project'
                                        ? t('globalSearch.projects')
                                        : type === 'task'
                                            ? t('globalSearch.tasks')
                                            : type === 'sprint'
                                                ? t('globalSearch.sprints')
                                                : t('globalSearch.epics') }), items.map((result) => {
                                    const globalIndex = results.indexOf(result);
                                    const isActive = globalIndex === activeIndex;
                                    return (_jsxs(Link, { to: result.projectId
                                            ? `/board/${result.projectId}`
                                            : `/board/${result.id}`, onClick: (e) => {
                                            e.preventDefault();
                                            handleSelect(result);
                                        }, className: `flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${isActive
                                            ? 'bg-primary text-white'
                                            : 'hover:bg-bg-secondary'}`, onMouseEnter: () => setActiveIndex(globalIndex), children: [_jsx("span", { className: "text-lg", children: TYPE_ICONS[result.type] }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("div", { className: "text-sm font-medium truncate", children: highlightMatch(result.title, debouncedQuery) }), result.subtitle && (_jsx("div", { className: "text-xs text-text-muted truncate", children: highlightMatch(result.subtitle, debouncedQuery) }))] }), result.status && (_jsx("span", { className: `text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[result.status] || 'bg-gray-100 text-gray-700'}`, children: result.status }))] }, result.id));
                                })] }, type));
                    }) })) : (_jsx("div", { className: "px-3 py-4 text-center text-text-muted text-sm", children: t('globalSearch.noResults') })) }))] }));
}
//# sourceMappingURL=GlobalSearch.js.map